import { Checkbox, FormHelperText, Grid } from '@mui/material';
import classnames from 'classnames';
import React from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Select, {
  components,
  GetOptionLabel,
  GetOptionValue,
  GroupBase,
  MenuPlacement,
  OptionsOrGroups
} from 'react-select';
import { useError } from '../helpers/hooks';

export type TSelectProps<T> = Omit<ControllerRenderProps, 'ref'> & {
  options?: OptionsOrGroups<T, GroupBase<T>>;
  getOptionLabel?: GetOptionLabel<T>;
  getOptionValue?: GetOptionValue<T>;
  label?: string;
  required?: boolean;
  isClearable?: boolean;
  isLoading?: boolean;
  controllerProps?: Omit<ControllerRenderProps, 'ref'>;
  postAction?: (e: any) => void;
  disabled?: boolean;
  menuPlacement?: MenuPlacement;
  menuWidth?: string; //viete posielat % aj px
  menuLimit?: number;
  tabIndex?: number;
};

export const XsMultiSearchSelect = <T extends unknown>(props: TSelectProps<T>) => {
  const {
    name,
    label,
    required,
    isClearable,
    isLoading,
    postAction,
    controllerProps,
    options,
    getOptionValue,
    getOptionLabel,
    disabled,
    menuPlacement = 'auto',
    menuWidth = '100%',
    menuLimit,
    tabIndex,
    ...rest
  } = props;

  let customStyles: any;

  const { error, helperText } = useError(name);
  const { t } = useTranslation();
  const formFieldValue = props.value;

  customStyles = {
    control: (styles: any, { menuIsOpen }: any) => {
      return {
        ...styles,
        '&:hover': {
          borderColor: '#000000 !important'
        },
        backgroundColor: '#FFFFFF',
        // border: `1px solid ${menuIsOpen ? '#000000de' : error ? '#ea7070' : '#c4c4c4'}`,
        borderWidth: 1,
        borderColor: menuIsOpen ? '#000000de' : error ? '#ea7070' : '#c4c4c4',
        borderStyle: disabled ? 'dashed' : 'solid',
        // border: `1px solid ${menuIsOpen ? '#000000de' : error ? '#ea7070' : disabled ? '#c4c4c4' : '#22232c'}`,
        borderTopLeftRadius: '4px',
        borderTopRightRadius: '4px',
        borderBottomLeftRadius: '4px',
        borderBottomRightRadius: '4px'
      };
    },
    menu: (provided: any, state: any) => ({
      ...provided,
      width: menuWidth,
      zIndex: '2'
    }),
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    dropdownIndicator: (styles: any, state: any) => {
      return {
        ...styles,
        color: '#54bebb',
        transform: state.selectProps.menuIsOpen && 'rotate(180deg)'
      };
    },
    multiValue: (styles: any) => {
      return {
        ...styles,
        borderRadius: '8px',
        color: '#22232c'
      };
    },
    option: (styles: any, { isFocused, isSelected, isHover }: any) => {
      return {
        ...styles,
        '&:hover': {
          backgroundColor: '#d3d3d3 !important'
        },
        backgroundColor: isSelected ? '#d3d3d3' : isHover ? '#d3d3d3' : undefined
      };
    }
  };

  const labelStyles: React.CSSProperties = {
    position: 'absolute',
    zIndex: 1,
    fontSize: '0.75rem',
    marginLeft: '10px',
    // color: error ? '#c6184e' : disabled ? 'rgba(0, 0, 0, 0.4)' : '#22232c',
    color: error ? '#c6184e' : '#22232c',
    top: '-7px',
    background: 'white',
    padding: '0px 6px 0px 4px'
  };

  const MenuList = (props: any) => {
    const childrenData = Array.isArray(props.children)
      ? props.children.slice(0, menuLimit ? menuLimit : 48)
      : props.children;
    return <components.MenuList {...props}>{childrenData}</components.MenuList>;
  };

  function Option(props: any) {
    return getOption(props);
  }

  function getOption(props: any) {
    const elementIdentifier: string = getOptionValue ? getOptionValue(props?.data) : props?.data?._id;
    const isChecboxChecked =
      formFieldValue &&
      formFieldValue?.find((val: any) => {
        const valueIdentifier = getOptionValue ? getOptionValue(val) : val?._id;
        return valueIdentifier === elementIdentifier;
      });

    return (
      <Grid
        container
        style={{
          padding: '0px',
          display: 'flex',
          alignItems: 'center'
        }}
        onClick={() => {
          props.selectOption(props.data);
        }}
      >
        <Grid xs={2} item>
          <Checkbox checked={isChecboxChecked} onChange={() => {}} />
        </Grid>
        <Grid
          xs={10}
          item
          buttonRef={props.innerRef}
          selected={props.isFocused}
          component='div'
          style={{
            fontWeight: 300,
            padding: 0,
            backgroundColor: 'white !important'
          }}
          {...props.innerProps}
        >
          <components.Option {...props} style={{ backgroundColor: 'white !important' }} />
        </Grid>
      </Grid>
    );
  }

  let selectComponents: Record<string, (props: any) => JSX.Element | null> = { MenuList, Option }; //isClearable
  if (disabled) {
    selectComponents = {
      ...selectComponents,
      MultiValueRemove: () => null,
      DropdownIndicator: () => null,
      IndicatorSeparator: () => null
    };
  }

  return (
    <div className='flex-col w-full relative'>
      {label && (
        <div className={classnames({ bold: required })} style={labelStyles}>
          {required ? `${label} *` : label}
        </div>
      )}
      <Select
        {...rest}
        tabIndex={tabIndex}
        noOptionsMessage={() => t('xs_searchSelect.no_options_message')}
        options={options}
        components={selectComponents}
        onChange={(e) => {
          rest.onChange(e);
          if (postAction && typeof postAction === 'function') {
            postAction(e);
          }
        }}
        placeholder={''}
        isMulti
        menuPlacement={menuPlacement}
        menuPosition={'absolute'}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        styles={customStyles}
        isClearable={false}
        isLoading={isLoading}
        isDisabled={disabled}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
      />
      {error && (
        <FormHelperText error={true} style={{ marginLeft: '14px' }}>
          {helperText}
        </FormHelperText>
      )}
    </div>
  );
};
